import { addDays, addMonths, diffDays, fromISO } from './dates'

/**
 * The rhythms a plan can have. There is no label here: this file is pure and
 * knows nothing about what language the app is in, so it carries the key and
 * whoever draws it says the word (`cadenceKey` → `t()`).
 */
export const CADENCES = [
  { value: 'once' },
  { value: 'weekly' },
  { value: 'fortnightly' },
  { value: 'monthly' },
  { value: 'quarterly' },
  { value: 'yearly' },
]

export const cadenceKey = (value) =>
  CADENCES.some((c) => c.value === value) ? `cadence.${value}` : value

const DAY_STEP = { weekly: 7, fortnightly: 14 }
const MONTH_STEP = { monthly: 1, quarterly: 3, yearly: 12 }

/** `n` steps of this cadence on from `anchor`. Budgets walk periods with it. */
export const stepBy = (cadence, anchor, n) => {
  const days = DAY_STEP[cadence]
  return days ? addDays(anchor, days * n) : addMonths(anchor, (MONTH_STEP[cadence] ?? 1) * n)
}

/** How many times a cadence lands in a year — used to spread yearly bills. */
export const perYear = (cadence) =>
  ({ once: 0, weekly: 52, fortnightly: 26, monthly: 12, quarterly: 4, yearly: 1 })[cadence] ?? 0

/** The plan's amount expressed as a monthly figure. */
export const monthlyEquivalent = (plan) => (plan.amount * perYear(plan.cadence)) / 12

/**
 * Every date a plan is due within [from, to] inclusive.
 * Bounded by the plan's own start/end dates.
 */
export function occurrences(plan, from, to) {
  const out = []
  if (!plan || plan.archived) return out
  const start = plan.start_date
  const stop = plan.end_date && plan.end_date < to ? plan.end_date : to

  if (plan.cadence === 'once') {
    if (start >= from && start <= stop) out.push(start)
    return out
  }

  const dayStep = DAY_STEP[plan.cadence]
  if (dayStep) {
    // Jump straight to the first occurrence inside the window.
    const behind = diffDays(start, from)
    const skip = behind > 0 ? Math.floor(behind / dayStep) : 0
    let cursor = addDays(start, skip * dayStep)
    while (cursor < from) cursor = addDays(cursor, dayStep)
    while (cursor <= stop) {
      out.push(cursor)
      cursor = addDays(cursor, dayStep)
    }
    return out
  }

  const monthStep = MONTH_STEP[plan.cadence] ?? 1
  const startDate = fromISO(start)
  const fromDate = fromISO(from)
  const monthsApart =
    (fromDate.getFullYear() - startDate.getFullYear()) * 12 +
    (fromDate.getMonth() - startDate.getMonth())
  // Every date is measured from the anchor, never from the previous one, so a
  // bill due on the 31st returns to the 31st after February rather than
  // creeping backwards a day a year.
  let step = Math.max(0, Math.floor(monthsApart / monthStep))
  let cursor = addMonths(start, step * monthStep)
  while (cursor < from) cursor = addMonths(start, ++step * monthStep)
  // A window is at most a handful of periods wide; the guard is for safety only.
  for (let i = 0; cursor <= stop && i < 400; i++) {
    out.push(cursor)
    cursor = addMonths(start, ++step * monthStep)
  }
  return out
}

/**
 * The date this plan's rhythm lands on nearest `date`, *ignoring* its start and
 * end — the slot a date belongs to, whether or not the plan reaches back that
 * far. A bill due on the 19th belongs to the 19th of its own month, and
 * knowing that is what stops an August payment being filed under September
 * merely because the plan was only written down in September.
 */
export function nearestSlot(plan, date) {
  if (!plan || !date) return null
  if (plan.cadence === 'once') return plan.start_date

  const nearest = (candidates) =>
    candidates.reduce((best, d) =>
      Math.abs(diffDays(d, date)) < Math.abs(diffDays(best, date)) ? d : best,
    )

  const dayStep = DAY_STEP[plan.cadence]
  if (dayStep) {
    const steps = Math.round(diffDays(plan.start_date, date) / dayStep)
    return nearest([steps - 1, steps, steps + 1].map((n) => addDays(plan.start_date, n * dayStep)))
  }

  const monthStep = MONTH_STEP[plan.cadence] ?? 1
  const start = fromISO(plan.start_date)
  const target = fromISO(date)
  const monthsApart =
    (target.getFullYear() - start.getFullYear()) * 12 + (target.getMonth() - start.getMonth())
  const steps = Math.round(monthsApart / monthStep)
  // Neighbours too: clamping a 31st into February moves a slot by days.
  return nearest([steps - 1, steps, steps + 1].map((n) => addMonths(plan.start_date, n * monthStep)))
}

/** Whether the plan is actually running on that date. */
export const covers = (plan, date) =>
  !!plan && !!date && date >= plan.start_date && (!plan.end_date || date <= plan.end_date)

/** The next date a plan is due on or after `from`, or null if it is finished. */
export function nextOccurrence(plan, from) {
  const horizon = addMonths(from, 13)
  return occurrences(plan, from, horizon)[0] ?? null
}
